/* ==========================================================================
   Jaipur Watch Company — header search (Omega-style).

   <jwc-search> owns the panel. Any [data-jwc-search-open] link in the
   header (and the legacy a[href="#drawer-search"] used by the 404 page)
   toggles it. Results come from the Section Rendering API:
   {{ routes.predictive_search_url }}?section_id=jwc-predictive-search,
   so prices, images and hidden products follow the theme's own Liquid.

   Paired with assets/jwc-search.css.
   ========================================================================== */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('jwc-search')) return;

  var RECENT_KEY = 'jwc:recent-searches';
  var RECENT_LIMIT = 6;
  var CACHE_LIMIT = 40;
  var DEBOUNCE_MS = 180;
  var LOADING_DELAY_MS = 160;
  var TRIGGERS = '[data-jwc-search-open], a[href="#drawer-search"]';
  var OTHER_DRAWERS = 'a[href*="#drawer-"]:not([href="#drawer-search"])';

  function icon(paths, size, className) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '" viewBox="0 0 24 24"' +
      ' fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"' +
      ' aria-hidden="true" focusable="false"' + (className ? ' class="' + className + '"' : '') + '>' + paths + '</svg>'
    );
  }

  var ICON_CLOCK = icon('<circle cx="12" cy="12" r="8.25"/><path d="M12 7.5V12l3 2"/>', 16, 'jwc-search__term-icon');
  var ICON_REMOVE = icon('<path d="M6 6l12 12M18 6 6 18"/>', 14);
  var ICON_ARROW = icon('<path d="M4.5 12h15M13.5 6l6 6-6 6"/>', 14);

  function normalise(term) {
    return term.replace(/\s+/g, ' ').trim();
  }

  function cssTime(element, property, fallback) {
    var value = window.getComputedStyle(element).getPropertyValue(property).trim();
    var number = parseFloat(value);
    if (Number.isNaN(number)) return fallback;
    return /ms$/.test(value) ? number : number * 1000;
  }

  class JwcSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.panel = this.querySelector('[data-jwc-search-panel]');
      this.form = this.querySelector('[data-jwc-search-form]');
      this.input = this.querySelector('[data-jwc-search-input]');
      this.clearButton = this.querySelector('[data-jwc-search-clear]');
      this.body = this.querySelector('[data-jwc-search-body]');
      this.idleView = this.querySelector('[data-jwc-search-idle]');
      this.resultsView = this.querySelector('[data-jwc-search-results]');
      this.skeletonView = this.querySelector('[data-jwc-search-skeleton]');
      this.status = this.querySelector('[data-jwc-search-status]');
      this.recentGroup = this.querySelector('[data-jwc-search-recent]');
      this.recentList = this.querySelector('[data-jwc-search-recent-list]');
      if (!this.panel || !this.form || !this.input || !this.body || !this.idleView || !this.resultsView) return;

      this.ready = true;
      this.isOpen = false;
      this.term = '';
      this.cache = new Map();
      this.controller = null;
      this.activeOption = null;
      this.inertElements = [];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onInputKeydown = this.onInputKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onReset = this.onReset.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);

      // Capture phase, so the header's search links never reach Taiga's
      // drawer delegate (which would open the old right-hand drawer).
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('pageshow', this.onPageShow);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onInputKeydown);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('reset', this.onReset);
      this.addEventListener('click', this.onClick);
      this.body.addEventListener('touchmove', this.onTouchMove, { passive: true });

      if (this.clearButton) this.clearButton.hidden = this.input.value.length === 0;
      this.renderRecent();
      this.syncTriggers(false);
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pageshow', this.onPageShow);
      this.abort();
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.debounceTimer);
      if (this.isOpen || this.classList.contains('is-closing')) {
        this.setInert(false);
        this.unlockScroll();
        document.documentElement.classList.remove('jwc-search-open', 'jwc-search-active');
      }
    }

    /* ---- Open / close --------------------------------------------------- */

    open(trigger, options) {
      var shouldFocus = !options || options.focus !== false;
      if (!this.ready || this.isOpen) return;

      this.isOpen = true;
      this.lastTrigger = trigger || null;
      window.clearTimeout(this.closeTimer);
      this.classList.remove('is-closing');

      this.measure();
      this.hidden = false;
      document.documentElement.classList.add('jwc-search-open', 'jwc-search-active');
      this.lockScroll();
      this.syncTriggers(true);
      this.renderRecent();

      // Focus inside the click itself: the caret is live at once and phones
      // raise the keyboard (they refuse focus() calls made after a delay).
      if (shouldFocus) {
        this.input.focus({ preventScroll: true });
        if (this.input.value) this.input.select();
      }

      // On the search page the field arrives filled with the current terms;
      // show their live results straight away.
      var existing = normalise(this.input.value);
      if (this.clearButton) this.clearButton.hidden = !existing;
      if (existing && this.resultsView.hidden) this.search(existing);

      this.setInert(true);
      this.panel.getBoundingClientRect(); // commit the closed state so the curtain animates
      this.classList.add('is-open');
      this.updateExpanded();
      this.dispatchEvent(new CustomEvent('jwc-search:open', { bubbles: true }));
    }

    close(options) {
      var restoreFocus = !options || options.restoreFocus !== false;
      var instant = options && options.instant;
      if (!this.ready || !this.isOpen) return;

      this.isOpen = false;
      this.abort();
      this.setLoading(false);
      this.setActive(null);
      this.classList.remove('is-open');
      document.documentElement.classList.remove('jwc-search-open');
      this.syncTriggers(false);
      this.setInert(false);
      this.updateExpanded();

      var hadFocus = this.contains(document.activeElement);
      if (hadFocus) this.input.blur();
      if (restoreFocus && hadFocus && this.lastTrigger && this.lastTrigger.isConnected) {
        this.lastTrigger.focus({ preventScroll: true });
      }

      var self = this;
      var finish = function () {
        window.clearTimeout(self.closeTimer);
        self.panel.removeEventListener('transitionend', onEnd);
        if (self.isOpen) return;
        self.classList.remove('is-closing');
        self.hidden = true;
        document.documentElement.classList.remove('jwc-search-active');
        self.unlockScroll();
      };
      var onEnd = function (event) {
        if (event.target === self.panel) finish();
      };

      if (instant || this.reducedMotion.matches) {
        finish();
      } else {
        this.classList.add('is-closing');
        this.panel.addEventListener('transitionend', onEnd);
        this.closeTimer = window.setTimeout(finish, cssTime(this, '--jwc-s-close', 440) + 120);
      }
      this.dispatchEvent(new CustomEvent('jwc-search:close', { bubbles: true }));
    }

    toggle(trigger) {
      if (this.isOpen) this.close();
      else this.open(trigger);
    }

    /* The panel hangs from the header's bottom edge. Measured from the
       header's section (Taiga slides the header itself in and out with a
       transform, which the section doesn't share), so a header still
       sliding back into view never leaves a gap. */
    measure() {
      var header = document.getElementById('MainHeader');
      var top = 0;
      if (header) {
        var section = header.closest('.shopify-section') || header.parentElement;
        var sectionTop = section ? section.getBoundingClientRect().top : 0;
        top = Math.max(0, Math.round(sectionTop + header.offsetTop + header.offsetHeight));
      }
      this.style.setProperty('--jwc-s-top', top + 'px');
    }

    lockScroll() {
      var root = document.documentElement;
      var scrollbar = Math.max(0, window.innerWidth - root.clientWidth);
      root.style.setProperty('--jwc-s-scrollbar', scrollbar + 'px');
      root.classList.add('jwc-search-lock');
    }

    unlockScroll() {
      var root = document.documentElement;
      root.classList.remove('jwc-search-lock');
      root.style.removeProperty('--jwc-s-scrollbar');
    }

    /* Everything outside the header and the panel is inert while open, so
       Tab and screen readers stay with the search. Skipped in the theme
       editor, where the merchant still needs to click other sections. */
    setInert(on) {
      this.inertElements.forEach(function (element) {
        element.inert = false;
      });
      this.inertElements = [];
      if (!on || (window.Shopify && window.Shopify.designMode)) return;

      var self = this;
      Array.prototype.forEach.call(document.body.children, function (element) {
        if (element.contains(self) || element.inert) return;
        if (element.matches('script, style, link, template, .section--header')) return;
        element.inert = true;
        self.inertElements.push(element);
      });
    }

    syncTriggers(open) {
      var label = open ? this.dataset.labelClose : this.dataset.labelOpen;
      document.querySelectorAll(TRIGGERS).forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', String(open));
        if (label) trigger.setAttribute('aria-label', label);
      });
    }

    /* ---- Events --------------------------------------------------------- */

    onDocumentClick(event) {
      var trigger = event.target.closest(TRIGGERS);
      if (trigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.toggle(trigger);
        return;
      }
      // Opening the menu or the cart closes the search first.
      if (this.isOpen && event.target.closest(OTHER_DRAWERS)) this.close({ restoreFocus: false });
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close();
      }
    }

    onResize() {
      if (this.isOpen) this.measure();
    }

    onPageShow(event) {
      // Coming back with the browser's Back button restores the old page
      // from memory; never return to a half-open panel and a locked page.
      if (event.persisted && this.isOpen) this.close({ restoreFocus: false, instant: true });
    }

    onSectionSelect(event) {
      if (event.target && event.target.contains(this)) this.open(null, { focus: false });
    }

    onSectionDeselect(event) {
      if (event.target && event.target.contains(this)) this.close({ restoreFocus: false });
    }

    // The theme editor re-renders the section after every setting change;
    // reopen so the merchant sees the edit straight away.
    onSectionLoad(event) {
      if (event.target && event.target.contains(this)) this.open(null, { focus: false });
    }

    onBlockSelect(event) {
      if (event.target && this.contains(event.target)) this.open(null, { focus: false });
    }

    onClick(event) {
      var target = event.target;

      if (target.closest('[data-jwc-search-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var remove = target.closest('[data-jwc-search-recent-remove]');
      if (remove) {
        event.preventDefault();
        event.stopPropagation();
        this.removeRecent(remove.getAttribute('data-jwc-search-recent-remove'));
        return;
      }

      if (target.closest('[data-jwc-search-recent-clear]')) {
        event.preventDefault();
        this.clearRecent();
        return;
      }

      var link = target.closest('a[href]');
      if (link && this.contains(link)) {
        var option = link.closest('[data-jwc-search-option]');
        var query = option && option.getAttribute('data-jwc-search-query');
        var typed = normalise(this.input.value);
        if (query) this.saveRecent(query);
        else if (typed) this.saveRecent(typed);
      }
    }

    onTouchMove() {
      // Scrolling results on a phone lowers the keyboard so more fit.
      if (document.activeElement === this.input) this.input.blur();
    }

    onInput() {
      var value = this.input.value;
      if (this.clearButton) this.clearButton.hidden = value.length === 0;
      window.clearTimeout(this.debounceTimer);

      var term = normalise(value);
      if (!term) {
        this.showIdle();
        return;
      }
      var self = this;
      this.debounceTimer = window.setTimeout(function () {
        self.search(term);
      }, DEBOUNCE_MS);
    }

    onInputKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        var options = this.options();
        if (!options.length) return;
        event.preventDefault();
        var index = options.indexOf(this.activeOption);
        var next;
        if (event.key === 'ArrowDown') next = index + 1;
        else next = index === -1 ? options.length - 1 : index - 1;
        this.setActive(next >= 0 && next < options.length ? options[next] : null);
        return;
      }

      if (event.key === 'Enter' && this.activeOption) {
        var link = this.activeOption.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          link.click();
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }

    onSubmit(event) {
      var term = normalise(this.input.value);
      if (!term) {
        event.preventDefault();
        this.input.focus();
        return;
      }
      this.saveRecent(term);
    }

    onReset(event) {
      event.preventDefault();
      this.input.value = '';
      if (this.clearButton) this.clearButton.hidden = true;
      this.showIdle();
      this.input.focus({ preventScroll: true });
    }

    /* ---- Results -------------------------------------------------------- */

    resultsUrl(term) {
      var url = new URL(this.dataset.resultsUrl || '/search/suggest', window.location.origin);
      var params = url.searchParams;
      params.set('q', term);
      params.set('section_id', this.dataset.sectionId || 'jwc-predictive-search');
      params.set('resources[type]', this.dataset.types || 'product,collection,query,page');
      params.set('resources[limit]', this.dataset.limit || '8');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', this.dataset.unavailable || 'last');
      params.set('resources[options][fields]', this.dataset.fields || 'title,product_type,variants.title,vendor,tag');
      return url.pathname + url.search;
    }

    searchPageUrl(term) {
      var url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('options[prefix]', 'last');
      return url.pathname + url.search;
    }

    cacheKey(term) {
      return normalise(term).toLowerCase();
    }

    search(term) {
      var key = this.cacheKey(term);
      var self = this;
      this.term = term;

      if (this.cache.has(key)) {
        this.abort();
        this.setLoading(false);
        this.renderResults(this.cache.get(key));
        return;
      }

      this.abort();
      var controller = new AbortController();
      this.controller = controller;
      if (this.resultsView.hidden) this.showView(this.skeletonView || this.resultsView);
      this.setLoading(true);

      fetch(this.resultsUrl(term), {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Search request failed: ' + response.status);
          return response.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var content = doc.querySelector('[data-jwc-search-results-content]');
          if (!content) throw new Error('Search results markup missing');
          self.remember(key, content.outerHTML);
          if (controller.signal.aborted || self.cacheKey(self.input.value) !== key) return;
          self.renderResults(content.outerHTML);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (self.cacheKey(self.input.value) === key) self.renderError(term);
        })
        .finally(function () {
          if (self.controller === controller) {
            self.controller = null;
            self.setLoading(false);
          }
        });
    }

    remember(key, markup) {
      this.cache.set(key, markup);
      if (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    setLoading(on) {
      window.clearTimeout(this.loadingTimer);
      if (on) {
        var self = this;
        this.loadingTimer = window.setTimeout(function () {
          self.classList.add('is-loading');
        }, LOADING_DELAY_MS);
        this.resultsView.setAttribute('aria-busy', 'true');
      } else {
        this.classList.remove('is-loading');
        this.resultsView.removeAttribute('aria-busy');
      }
    }

    renderResults(markup) {
      this.resultsView.innerHTML = markup;
      var content = this.resultsView.firstElementChild;
      if (content) this.applySettings(content);

      var count = content ? content.querySelectorAll('[data-jwc-search-option]').length : 0;
      if (count === 0) this.renderEmpty();
      this.showView(this.resultsView);

      // The Doubly currency app restyles prices ("Rs. 45,000.00" → "₹ 45,000")
      // only after page events; ask it to convert the new results right away.
      if (typeof window.doublyChangeEvent === 'function') window.doublyChangeEvent();

      var message;
      if (count === 0) message = this.dataset.textNoResults || 'No results for “[terms]”';
      else if (count === 1) message = this.dataset.textResultsOne || '1 result for “[terms]”';
      else message = this.dataset.textResults || '[count] results for “[terms]”';
      this.announce(message.replace('[count]', count).replace('[terms]', this.term));
    }

    /* The results section only carries markup; the panel's settings (from
       the theme editor) decide what is hidden and what it says. */
    applySettings(content) {
      var self = this;
      var hidden = {
        collection: this.handleList(this.dataset.hiddenCollections),
        page: this.handleList(this.dataset.hiddenPages)
      };

      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-kind]'), function (item) {
        var list = hidden[item.getAttribute('data-jwc-search-kind')];
        var handle = (item.getAttribute('data-jwc-search-handle') || '').toLowerCase();
        if (list && list.indexOf(handle) !== -1) item.remove();
      });

      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-max]'), function (list) {
        var max = Number(list.getAttribute('data-jwc-search-max'));
        if (!max) return;
        Array.prototype.slice.call(list.querySelectorAll('[data-jwc-search-option]'), max).forEach(function (item) {
          item.remove();
        });
      });

      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-group]'), function (group) {
        if (!group.querySelector('[data-jwc-search-option]')) group.remove();
      });

      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-text]'), function (element) {
        var value = self.text(element.getAttribute('data-jwc-search-text'));
        if (value) element.textContent = value.replace('[terms]', self.term);
      });

      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-count]'), function (element) {
        var count = Number(element.getAttribute('data-jwc-search-count'));
        var word = count === 1 ? self.dataset.textCountOne : self.dataset.textCount;
        if (word) element.textContent = count + ' ' + word;
      });
    }

    // data-text-view-all → text('view-all')
    text(key) {
      var name = 'text' + key.replace(/(^|-)([a-z])/g, function (match, dash, letter) {
        return letter.toUpperCase();
      });
      return this.dataset[name];
    }

    handleList(value) {
      return String(value || '')
        .toLowerCase()
        .split(/[\s,]+/)
        .filter(Boolean);
    }

    /* Nothing matched (or only hidden things did): the panel's own template,
       worded in the theme editor, plus popular searches and bestsellers. */
    renderEmpty() {
      var self = this;
      var content = document.createElement('div');
      content.setAttribute('data-jwc-search-results-content', '');
      content.setAttribute('data-count', '0');

      var template = this.querySelector('template[data-jwc-search-empty-template]');
      if (template) content.append(template.content.cloneNode(true));
      Array.prototype.forEach.call(content.querySelectorAll('[data-jwc-search-terms]'), function (element) {
        element.textContent = element.textContent.replace('[terms]', self.term);
      });

      this.resultsView.replaceChildren(content);
      this.fillEmptyState();
    }

    renderError(term) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-jwc-search-results-content', '');
      wrap.setAttribute('data-count', '0');

      var box = document.createElement('div');
      box.className = 'jwc-search__empty';

      var title = document.createElement('p');
      title.className = 'jwc-search__empty-title';
      title.textContent = this.dataset.textError || 'Live results are taking a moment.';

      var text = document.createElement('p');
      text.className = 'jwc-search__empty-text';
      text.textContent = this.dataset.textErrorHint || 'Press Enter to see every result on the search page.';

      var link = document.createElement('a');
      link.className = 'jwc-search__more';
      link.href = this.searchPageUrl(term);
      link.textContent = this.dataset.textViewAll || 'View all results';
      link.insertAdjacentHTML('beforeend', ICON_ARROW);

      box.append(title, text, link);
      wrap.append(box);
      this.resultsView.replaceChildren(wrap);
      this.showView(this.resultsView);
    }

    /* No results: point to the merchant's popular searches and bestsellers
       (managed once, in the panel's own settings) instead of a dead end. */
    fillEmptyState() {
      var terms = this.resultsView.querySelector('[data-jwc-search-empty-terms]');
      if (terms) {
        var popular = this.idleView.querySelectorAll('[data-jwc-search-popular] a[href]');
        var pills = Array.prototype.map.call(popular, function (source) {
          var item = document.createElement('li');
          var pill = document.createElement('a');
          pill.className = 'jwc-search__pill';
          pill.href = source.getAttribute('href');
          pill.textContent = source.textContent.trim();
          item.append(pill);
          return item;
        });
        terms.replaceChildren.apply(terms, pills);
        terms.hidden = pills.length === 0;
      }

      var fallback = this.resultsView.querySelector('[data-jwc-search-empty-products]');
      var featured = this.idleView.querySelector('[data-jwc-search-featured] [role="listbox"]');
      if (fallback) {
        var list = fallback.querySelector('[role="listbox"]');
        var clones = featured
          ? Array.prototype.map.call(featured.children, function (item, index) {
              var copy = item.cloneNode(true);
              copy.id = 'JwcSearchFallback-' + (index + 1);
              copy.setAttribute('aria-selected', 'false');
              return copy;
            })
          : [];
        if (list) list.replaceChildren.apply(list, clones);
        fallback.hidden = clones.length === 0;
      }
    }

    showIdle() {
      this.abort();
      this.setLoading(false);
      this.term = '';
      this.renderRecent();
      if (this.idleView.hidden) this.showView(this.idleView);
      this.announce('');
    }

    showView(view) {
      var wasHidden = view.hidden;
      [this.idleView, this.resultsView, this.skeletonView].forEach(function (candidate) {
        if (candidate) candidate.hidden = candidate !== view;
      });
      if (wasHidden) {
        view.classList.remove('is-entering');
        void view.offsetWidth;
        view.classList.add('is-entering');
        this.body.scrollTop = 0;
      }
      this.setActive(null);
      this.updateExpanded();
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = message;
    }

    /* ---- Keyboard: arrows move through every visible option ------------- */

    options() {
      var view = [this.idleView, this.resultsView].find(function (candidate) {
        return candidate && !candidate.hidden;
      });
      if (!view) return [];
      return Array.prototype.filter.call(view.querySelectorAll('[data-jwc-search-option]'), function (option) {
        return option.getClientRects().length > 0;
      });
    }

    setActive(option) {
      if (this.activeOption) this.activeOption.setAttribute('aria-selected', 'false');
      this.activeOption = option || null;

      if (!option) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      option.setAttribute('aria-selected', 'true');
      if (option.id) this.input.setAttribute('aria-activedescendant', option.id);

      // Keep the option in view inside the panel (never scroll the page).
      var bodyRect = this.body.getBoundingClientRect();
      var rect = option.getBoundingClientRect();
      if (rect.top < bodyRect.top + 8) this.body.scrollTop -= bodyRect.top + 8 - rect.top;
      else if (rect.bottom > bodyRect.bottom - 8) this.body.scrollTop += rect.bottom - (bodyRect.bottom - 8);
    }

    updateExpanded() {
      var view = null;
      if (this.isOpen) {
        view = [this.idleView, this.resultsView].find(function (candidate) {
          return candidate && !candidate.hidden;
        });
      }
      var lists = view
        ? Array.prototype.filter.call(view.querySelectorAll('[role="listbox"][id]'), function (list) {
            return list.children.length > 0 && !list.closest('[hidden]');
          })
        : [];
      this.input.setAttribute('aria-expanded', String(lists.length > 0));
      if (lists.length) {
        this.input.setAttribute(
          'aria-controls',
          lists
            .map(function (list) {
              return list.id;
            })
            .join(' ')
        );
      }
    }

    /* ---- Recent searches (this browser only) ---------------------------- */

    readRecent() {
      try {
        var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        if (!Array.isArray(list)) return [];
        return list
          .filter(function (term) {
            return typeof term === 'string' && normalise(term);
          })
          .slice(0, RECENT_LIMIT);
      } catch (error) {
        return [];
      }
    }

    writeRecent(list) {
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT)));
      } catch (error) {
        /* Private mode or storage disabled: recent searches just stay off. */
      }
    }

    saveRecent(term) {
      if (this.dataset.recent === 'false') return;
      var clean = normalise(term).slice(0, 80);
      if (clean.length < 2) return;
      var list = this.readRecent().filter(function (existing) {
        return existing.toLowerCase() !== clean.toLowerCase();
      });
      list.unshift(clean);
      this.writeRecent(list);
    }

    removeRecent(term) {
      this.writeRecent(
        this.readRecent().filter(function (existing) {
          return existing !== term;
        })
      );
      this.renderRecent();
      this.updateExpanded();
      this.input.focus({ preventScroll: true });
    }

    clearRecent() {
      this.writeRecent([]);
      this.renderRecent();
      this.updateExpanded();
      this.input.focus({ preventScroll: true });
    }

    renderRecent() {
      if (!this.recentGroup || !this.recentList) return;
      if (this.dataset.recent === 'false') {
        this.recentGroup.hidden = true;
        return;
      }

      var self = this;
      var removeLabel = this.dataset.textRemove || 'Remove “[terms]” from recent searches';
      var items = this.readRecent().map(function (term, index) {
        var item = document.createElement('li');
        item.className = 'jwc-search__option';
        item.id = 'JwcSearchRecent-' + (index + 1);
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('data-jwc-search-option', '');
        item.setAttribute('data-jwc-search-query', term);

        var link = document.createElement('a');
        link.className = 'jwc-search__term';
        link.href = self.searchPageUrl(term);
        link.tabIndex = -1;
        link.insertAdjacentHTML('afterbegin', ICON_CLOCK);
        var text = document.createElement('span');
        text.className = 'jwc-search__term-text';
        text.textContent = term;
        link.append(text);

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'jwc-search__remove';
        remove.tabIndex = -1;
        remove.setAttribute('data-jwc-search-recent-remove', term);
        remove.setAttribute('aria-label', removeLabel.replace('[terms]', term));
        remove.innerHTML = ICON_REMOVE;

        item.append(link, remove);
        return item;
      });

      this.recentList.replaceChildren.apply(this.recentList, items);
      this.recentGroup.hidden = items.length === 0;
    }
  }

  customElements.define('jwc-search', JwcSearch);
})();
